import BaseLayer from './BaseLayer';
import jQuery from 'jquery';
import Utils from 'helpers/Utils';
import ClusterHelper from 'helpers/ClusterHelper';
import _ from 'underscore';

const CLUSTER_ID = 'cluster_id',
  POINT_COUNT = 'point_count';

export default BaseLayer.extend({

  constructor(options) {
    BaseLayer.prototype.constructor.call(this, options);
    this.markers = [];
    if (this.options.cluster) {
      this.clusterHelper = new ClusterHelper(options);
    }
  },

  initialize(options) {
    BaseLayer.prototype.initialize.call(this, options);
    this._bindMarkerLayerEvents();
  },

  _bindMarkerLayerEvents() {
    //The provider triggers this event with an array of feature in GeoJSON
    this.listenTo(this.options.provider, 'PROCESS_FEATURES', (features, provider) => {
      this._processFeatures(features);
      this.widget.bus.trigger('LAYER_LOADED', this, features);
    });
  },

  /**
   * Process the new features loaded:
   * 1- Process pre filters
   * 2- Cluster the features
   * 3- Process post filters
   * 4- Remove old markers
   * 5- Create new markers
   * @param {GeoJSON} features features in a GeoJSON object
   */
  _processFeatures(features) {
    this._processFilters(this.options.preFilters, features).then((featuresPrefiltered) => {
      if (this.options.cluster) {
        //Cluster the features, it returns an array of Features with an attribute named "cluster" in each feature
        //that indicates if it is a cluster or not.
        featuresPrefiltered = this.clusterHelper.clusterFeatures(featuresPrefiltered);
      }
      this._processFilters(this.options.postFilters, featuresPrefiltered).then((featuresPostfiltered,
        isFilterActivated) => {
        this._removeMarkers(featuresPostfiltered, isFilterActivated);
        this._createMarkers(featuresPostfiltered);
        this.widget.bus.trigger('UPDATED_MARKERS', this);
      });
    });
  },

  /**
   * This method is the same to process the preFilters and the postFilters
   * @param {Filter[]} filters An array with the filters objects
   * @param {Feature[]} features An array with the features
   */
  _processFilters(filters, features) {
    let deferred = jQuery.Deferred(),
      promises = [];
    //If there are not filters, we return the features directly.
    //If not, we filter the features and return only the features filtered
    if (filters.length > 0) {
      for (let i = 0; i < filters.length; i++) {
        promises.push(filters[i].filterFeatures(features));
      }
      jQuery.when.apply(jQuery, promises).then((featuresFiltered, isFilterActivated) => {
        deferred.resolve(featuresFiltered, isFilterActivated);
      });
    } else {
      deferred.resolve(features, false);
    }
    return deferred;
  },

  /**
   * Transform the features in markers
   * @param {Feature[]} features 
   */
  _createMarkers(features) {
    _.each(features, (feature) => {
      //We check that the marker isn't created
      let marker = this.getMarkerFromId(this._getFeatureId(feature));
      if (!marker) {
        //The marker is not created in a previous processment, so, we create the marker.
        //Once the marker is created, we add the listeners and draw it.
        marker = this._createMarker(feature);
        this._addListeners(marker);
        this._showMarker(marker);
        this.markers.push(marker);
      }
      this.trigger('createdMarker', marker, feature);
    });
  },

  /**
   * Create the marker from the feature
   * @param {Feature} feature The feature to transform in marker
   */
  _createMarker(feature) {
    //This properties are shared for a marker and a cluster
    let marker = new google.maps.Marker({
      id: this._getFeatureId(feature),
      layerId: this.options.id,
      position: new google.maps.LatLng(feature.geometry.coordinates[1], feature.geometry.coordinates[0]),
      properties: feature.properties,
      optimized: !this.widget.config.debugEnabled,
      clickable: this.options.markersClickable,
      zIndex: this.options.markersZIndex ? this.options.markersZIndex : 1
    });
    if (feature.properties.cluster) {
      //The cluster lib create a cluster_id and point_count in the properties
      marker.setIcon(this.clusterHelper.getIconCluster(this.widget));
      marker.setLabel(this.clusterHelper.getLabelCluster(feature.properties[POINT_COUNT]));
    } else {
      marker.setIcon(this._getIconMarker(feature.properties, 'default'));
    }
    return marker;
  },

  /**
   * Get the icon of the marker according to the status 'default', 'hover' or 'selected'
   * @param {object} properties The properties of the feature
   * @param {string} status 'default', 'hover', selected'
   */
  _getIconMarker(properties, status = 'default') {
    //Get the icon if the marker changes according to a property value. If not, get the same icon for every marker
    let iconMarker = this.options.iconMarkerByType ?
      this._getIconMarkerByType(properties, status) :
      this.options.iconMarker[status];

    let icon;
    if (iconMarker.url) {
      icon = {
        url: Utils.getImgUrl(iconMarker.url, this.widget)
      };
      if (iconMarker.anchor) {
        icon.anchor = new google.maps.Point(iconMarker.anchor.x, iconMarker.anchor.y);
      }
      if (iconMarker.scaledSize) {
        icon.scaledSize = new google.maps.Size(iconMarker.scaledSize.width, iconMarker.scaledSize.height);
      }
    } else {
      icon = iconMarker;
    }
    return icon;
  },

  /**
   * Get the icon if the marker changes according to a property value
   * @param {object} properties The properties of the feature
   * @param {string} status 'default', 'hover', selected'
   */
  _getIconMarkerByType(properties, status) {
    let iconMarker, found = false;
    _.each(this.options.iconMarkerByType, (config, field) => {
      if (found) {
        return;
      }
      let key = properties[field];
      if (_.isNull(config[key]) || _.isUndefined(config[key]) || _.isNull(config[key][status]) ||
        _.isUndefined(config[key][status])) {
        iconMarker = this.options.iconMarker[status];
      } else {
        iconMarker = config[key][status];
        found = true;
      }
    });
    return iconMarker;
  },

  /**
   * Add the listeners (click and hover) of the markers
   * @param {google.maps.Marker} marker 
   */
  _addListeners(marker) {
    if (marker.clickable) {
      this._addClickListener(marker);
    }
    //The clusters don't have hover listener by default
    if (!marker.properties.cluster && this.options.iconMarker.hover) {
      this._addHoverListeners(marker);
    }
  },

  /**
   * Add click listener
   * @param {google.maps.Marker} marker the marker clicked
   */
  _addClickListener(marker) {
    marker.clickListener = google.maps.event.addListener(marker, 'click', (event) => {
      if (event instanceof Event) {
        event.stopPropagation();
      }
     
      if (marker.properties.cluster) {
        this._clusterClicked(marker);
      } else {
        this._markerClicked(marker, event);
      }
    });
  },

  /**
   * When a cluster is clicked, the cluster lib calculates the next zoom
   * @param {google.maps.Marker} cluster the cluster clicked
   */
  _clusterClicked(cluster) {
    this.clusterHelper.setClusterExpansion(cluster.properties[CLUSTER_ID]);
  },

  /**
   * Deselect the previous marker selected, and select the new marker clicked
   * @param {google.maps.Marker} marker the marker clicked
   */
  _markerClicked(marker, e) {
    this.deselectMarker();
    this.selectMarker(marker, e);
  },

  /**
   * Added the listeners when the mouse is over the marker and out of it.
   * @param {google.maps.Marker} marker the marker hovers in/out
   */
  _addHoverListeners(marker) {
    marker.mouseoverListener = google.maps.event.addListener(marker, 'mouseover', () => {
      this.setHoverStatus(marker);
    });
    marker.mouseoutListener = google.maps.event.addListener(marker, 'mouseout', () => {
      this.setDefaultStatus(marker);
    });
  },

  /**
   * Change the icon to the hover style and add a big zIndex to show over the other markers
   * @param {google.maps.Marker} marker the marker hovers in/out
   */
  setHoverStatus(marker) {
    if (!marker.isSelected) {
      marker.setIcon(this._getIconMarker(marker.properties, 'hover'));
      marker.setZIndex(9999);
    }
  },

  /**
   * Return to the default status
   * @param {google.maps.Marker} marker the marker
   */
  setDefaultStatus(marker) {
    if (!marker.isSelected) {
      marker.setIcon(this._getIconMarker(marker.properties, 'default'));
      marker.setZIndex(this.options.markersZIndex ? this.options.markersZIndex : 1);
    }
  },

  /**
   * Select a marker and change its icon if it is needed
   * @param {google.maps.Marker} marker the marker
   */
  selectMarker(marker, e) {
    if (this.options.iconMarker.selected || this.options.iconMarkerByType) {
      marker.setIcon(this._getIconMarker(marker.properties, 'selected'));
    }
    marker.setZIndex(9999);
    marker.isSelected = true;
    this.currentSelectedMarker = marker;
    this.widget.bus.trigger('CLICK_ON_MARKER', marker, e);
  },

  /**
   * Deselect the marker clicked previously
   */
  deselectMarker() {
    if (this.currentSelectedMarker) {
      this.currentSelectedMarker.setIcon(this._getIconMarker(this.currentSelectedMarker.properties, 'default'));
      this.currentSelectedMarker.setZIndex(this.options.markersZIndex ? this.options.markersZIndex : 1);
      this.currentSelectedMarker.isSelected = false;
      this.currentSelectedMarker = null;
    }
  },

  /**
   * Show the marker
   * @param {google.maps.Marker} marker the marker to be shown on the map
   */
  _showMarker(marker) {
    //This condition is to avoid flickering
    if (!marker.getMap() && this.options.model.isVisible()) {
      marker.setMap(this.widget.map);
    }
  },

  /**
   * Hide the marker
   * @param {google.maps.Marker} marker the marker to be hidden on the map
   */
  _hideMarker(marker) {
    marker.setMap(null);
  },

  /**
   * Remove markers on the map
   * @param {google.maps.Marker[]} markers the markers to remove
   */
  _removeAllMarkers(markers) {
    for (let i = 0; i < markers.length; i++) {
      this._removeMarker(markers[i]);
      delete this.markers[i];
    }
  },

  /**
   * Remove the markers that are not in the features, if cleanMarkers is true
   * @param {Feature[]} featuresPostfiltered features that will be shown on the map
   * @param {boolean} isFilterActivated to know if the travel time filter is activated, if not, delete the labels
   */
  _removeMarkers(featuresPostfiltered, isFilterActivated) {
    if (!this.options.cleanMarkers) {
      return;
    }
    let markersOnTheMap = [],
      markersToClean = [];
    if (this.markers.length === 0) {
      //If there are not markers to remove, finish this method
      return;
    } else if (featuresPostfiltered && featuresPostfiltered.length > 0) {
      //We get the features that already are shown on the map to keep them on the map and remove the others
      _.each(featuresPostfiltered, (feature) => {
        let featureId = this._getFeatureId(feature);
        let marker = this.getMarkerFromId(featureId);
        if (marker) {
          markersOnTheMap.push(marker);
          if (!isFilterActivated) {
            this.trigger('resetMarker', marker, feature);
          }
        }
      });
    } else {
      //If the features array is empty, the map will be empty, so we will remove all the markers
      markersOnTheMap = [];
    }
    //markersToClean is the markers that are not in the features loaded, so they will be removed
    markersToClean = _.difference(this.markers, markersOnTheMap);
    for (let i = 0; i < markersToClean.length; i++) {
      this._removeMarker(markersToClean[i]);
      delete markersToClean[i];
    }
    //The markers that remain on the map
    this.markers = markersOnTheMap;
  },

  /**
   * Remove a marker from the map
   * @param {google.maps.Marker} marker the marker to remove from the map
   */
  _removeMarker(marker) {
    marker.setMap(null);
    this.trigger('removedMarker', marker);
  },

  /**
   * Remove markers and clusters on the map
   */
  remove() {
    this._removeAllMarkers(this.markers);
    this.currentSelectedMarker = null;
    this.markers = [];
  },

  /**
   * Hide the markers
   */
  hide() {
    _.each(this.markers, (marker) => {
      marker.setMap(null);
      this.trigger('hiddenMarker', marker);
    });
  },

  /**
   * Show the markers only if we don't use clusters
   */
  show() {
    //If we use clusters, the markers/clusters must be recalculated, not only show the previous markers processed
    if (!this.options.cluster) {
      _.each(this.markers, (marker) => {
        this._showMarker(marker);
        this.trigger('shownMarker', marker);
      });
    }
  },

  /**
   * Get a marker from id
   * @param {string} id the id of the marker
   * @return the marker and its index in the markers array
   */
  getMarkerFromId(id) {
    let result = null;
    for (var i = 0; i < this.markers.length; i++) {
      if (this.markers[i].id === id) {
        result = this.markers[i];
        result.index = i;
        break;
      }
    }
    return result;
  },

  /**
   * Get the feature Id, different for cluster and marker
   * @param {Feature} feature the feature
   * @return the id of the feature
   */
  _getFeatureId(feature) {
    let id;
    if (feature.properties.cluster) {
      id = this.clusterHelper.getClusterId(feature);
    } else {
      id = feature.properties[this.options.featureId];
    }
    return id;
  }

});



// WEBPACK FOOTER //
// ./lmm-widget/src/layers/types/_base/MarkerLayer.js